import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton } from "@/components/ui-system";
import { toast } from "sonner";
import {
  IndianRupee, Zap, ListOrdered, Sparkles, Loader2, ChevronUp, ChevronDown, Truck, Save, Hand, Info,
} from "lucide-react";

const MODES = [
  { value: "manual", label: "Manual", icon: Hand, desc: "You pick the courier yourself after creating each order. (Default.)" },
  { value: "recommended", label: "EdgeLMS Recommended", icon: Sparkles, desc: "EdgeLMS picks the best courier and ships each order automatically." },
  { value: "cheapest", label: "Cheapest", icon: IndianRupee, desc: "Auto-ship each order with the lowest-priced courier." },
  { value: "fastest", label: "Fastest", icon: Zap, desc: "Auto-ship each order with the fastest courier by delivery time." },
  { value: "custom", label: "Custom priority", icon: ListOrdered, desc: "Rank couriers yourself; auto-ship with the highest-ranked serviceable one." },
];

/**
 * Seller setting: how a courier is recommended/pre-selected when creating a B2C
 * order. White-label — only canonical courier names/logos are shown.
 */
const CourierSelectionCriteria = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("manual");
  const [order, setOrder] = useState([]); // ranked courier codes (custom mode)
  const [couriers, setCouriers] = useState([]); // {code, name, logo_url, modes}
  const [recommendedPriority, setRecommendedPriority] = useState([]); // ordered courier codes

  const courierByCode = useMemo(
    () => Object.fromEntries(couriers.map((c) => [c.code, c])),
    [couriers]
  );

  const fetchCriteria = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/courier-criteria`);
      const available = data.available_couriers || [];
      setMode(data.mode || "manual");
      setCouriers(available);
      setRecommendedPriority(data.recommended_priority || []);
      // Saved order first (only still-valid codes), then any couriers not yet ranked.
      const saved = (data.priority_order || []).filter((c) => available.some((x) => x.code === c));
      const rest = available.map((c) => c.code).filter((c) => !saved.includes(c));
      setOrder([...saved, ...rest]);
    } catch (e) {
      toast.error("Failed to load courier criteria");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  const move = (idx, dir) => {
    setOrder((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/b2c/seller/courier-criteria`, {
        mode,
        priority_order: mode === "custom" ? order : [],
      });
      toast.success("Courier selection criteria saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save criteria");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-800">B2C Courier Priority Setting</h1>
          <p className="mt-1 text-sm text-slate-500">
            Choose how a courier is assigned when you create a B2C order. In Manual mode you pick a courier per order; any other mode ships each order automatically on creation.
          </p>
        </div>

        {loading ? (
          <LoadingSkeleton variant="table" rows={4} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMode(m.value)}
                    className={`rounded-xl border p-4 text-left transition ${
                      active ? "border-brand bg-brand/5 ring-2 ring-brand/20" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`criteria-mode-${m.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${active ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="font-semibold text-slate-800">{m.label}</span>
                      {m.value === "manual" && (
                        <Badge variant="outline" className="text-[10px]">Default</Badge>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">{m.desc}</p>
                  </button>
                );
              })}
            </div>

            {mode === "manual" ? (
              <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="criteria-info">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                <span>You'll pick a courier for each order after creating it — nothing ships automatically.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="criteria-info">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                <span>
                  Each order will be <strong>shipped automatically on creation</strong> — a courier is booked and your
                  wallet is charged, with no manual step. Choose <strong>Manual</strong> if you'd rather review each order
                  before it ships.
                </span>
              </div>
            )}

            {mode === "recommended" && (
              <Card>
                <CardContent className="p-4">
                  {recommendedPriority.length > 0 ? (
                    <>
                      <p className="mb-3 text-sm text-slate-600">
                        EdgeLMS recommends these couriers in priority order — the first one that can service the lane is used.
                      </p>
                      <div className="divide-y rounded-lg border" data-testid="recommended-priority-list">
                        {recommendedPriority.map((code, idx) => {
                          const c = courierByCode[code];
                          return (
                            <div key={code} className="flex items-center gap-3 px-3 py-2" data-testid={`recommended-${code}`}>
                              <span className="w-6 text-center text-sm font-bold tabular-nums text-brand">{idx + 1}</span>
                              {c?.logo_url ? (
                                <img src={c.logo_url} alt="" className="h-7 w-7 rounded border bg-white object-contain p-0.5" />
                              ) : (
                                <span className="flex h-7 w-7 items-center justify-center rounded bg-slate-100 text-slate-400"><Truck className="h-4 w-4" /></span>
                              )}
                              <span className="flex-1 truncate text-sm font-medium text-slate-700">{c?.name || code}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-600">
                      EdgeLMS will recommend the best courier for each order automatically.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {mode === "custom" && (
              <Card>
                <CardContent className="p-4">
                  <p className="mb-3 text-xs text-slate-500">
                    Rank couriers with the up/down arrows. The highest-ranked courier that can service the lane is recommended.
                  </p>
                  <div className="divide-y rounded-lg border">
                    {order.map((code, idx) => {
                      const c = courierByCode[code];
                      if (!c) return null;
                      return (
                        <div key={code} className="flex items-center gap-3 px-3 py-2" data-testid={`criteria-courier-${code}`}>
                          <span className="w-6 text-center text-sm font-semibold tabular-nums text-slate-400">{idx + 1}</span>
                          {c.logo_url ? (
                            <img src={c.logo_url} alt="" className="h-7 w-7 rounded border bg-white object-contain p-0.5" />
                          ) : (
                            <span className="flex h-7 w-7 items-center justify-center rounded bg-slate-100 text-slate-400"><Truck className="h-4 w-4" /></span>
                          )}
                          <span className="flex-1 truncate text-sm font-medium text-slate-700">{c.name}</span>
                          <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`criteria-up-${code}`}>
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === order.length - 1} onClick={() => move(idx, 1)} data-testid={`criteria-down-${code}`}>
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="criteria-save">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save criteria
              </Button>
            </div>
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default CourierSelectionCriteria;
